import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Calculator, RotateCcw } from 'lucide-react';
import { driverPayCalcApi } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const lineAmount = (l) => Math.round((l.method === 'fixed' ? Number(l.rate || 0) : (Number(l.payout_amount || 0) * Number(l.rate || 0)) / 100) * 100) / 100;

/** Pick a driver's loads (any recent week), set fixed / % per load, save as this week's pay. */
export const PayCalculatorDialog = ({ open, onOpenChange, driver, weekStart, onSaved }) => {
  const [weeksBack, setWeeksBack] = useState('4');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkPct, setBulkPct] = useState('');

  useEffect(() => {
    if (!open) { setRows([]); return; }
    if (!driver) return;
    setLoading(true);
    driverPayCalcApi.get(driver.id, weekStart, Number(weeksBack))
      .then((r) => {
        // Keep unsaved edits/selections when widening or narrowing the range
        setRows((prev) => {
          const edited = Object.fromEntries(prev.map((p) => [p.id, p]));
          return r.data.loads.map((l) => (edited[l.id] ? { ...l, selected: edited[l.id].selected, method: edited[l.id].method, rate: edited[l.id].rate } : l));
        });
        setMeta(r.data);
        setBulkPct((b) => b || String(r.data.default_pct || ''));
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load loads'))
      .finally(() => setLoading(false));
  }, [open, driver, weekStart, weeksBack]);

  const update = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const selected = rows.filter((r) => r.selected);
  const total = useMemo(() => selected.reduce((s, r) => s + lineAmount(r), 0), [selected]);
  const selectWhere = (fn) => setRows((rs) => rs.map((r) => ({ ...r, selected: fn(r) })));
  const applyPctToSelected = () => setRows((rs) => rs.map((r) => (r.selected && !r.is_block ? { ...r, method: 'percentage', rate: Number(bulkPct || 0) } : r)));

  const save = async () => {
    setSaving(true);
    try {
      await driverPayCalcApi.save(driver.id, {
        week_start: weekStart,
        lines: selected.map((r) => ({ load_id: r.id, method: r.method, rate: Number(r.rate || 0) })),
      });
      toast.success(`Saved ${money(total)} as ${driver.name}'s pay for this week`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      await driverPayCalcApi.reset(driver.id, weekStart);
      toast.success('Reset to automatic pay');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] max-h-[92vh] overflow-y-auto" data-testid="pay-calc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl"><Calculator className="h-5 w-5 text-blue-600" /> Calculate pay · {driver?.name}</DialogTitle>
          <DialogDescription className="text-sm">
            Week of {weekStart}. Tick the loads to pay, set each to a fixed amount or % of gross. Block loads (B-…) default to the driver's block pay{meta.block_pay ? ` (${money(meta.block_pay)})` : ' (not set on profile)'}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={weeksBack} onValueChange={setWeeksBack}>
            <SelectTrigger className="w-[190px]" data-testid="pay-calc-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">This week only</SelectItem>
              <SelectItem value="2">This week + 2 prior</SelectItem>
              <SelectItem value="4">This week + 4 prior</SelectItem>
              <SelectItem value="8">This week + 8 prior</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => selectWhere(() => true)} data-testid="pay-calc-select-all">Select all</Button>
          <Button variant="outline" size="sm" onClick={() => selectWhere((r) => r.week_start === weekStart)} data-testid="pay-calc-select-week">This week's loads</Button>
          <Button variant="outline" size="sm" onClick={() => selectWhere(() => false)} data-testid="pay-calc-select-none">Clear</Button>
          <div className="flex items-center gap-1.5 ml-auto">
            <Input type="number" step="0.1" className="w-24" value={bulkPct} onChange={(e) => setBulkPct(e.target.value)} data-testid="pay-calc-bulk-pct" />
            <Button size="sm" variant="secondary" onClick={applyPctToSelected} data-testid="pay-calc-apply-pct">Apply % to selected</Button>
          </div>
        </div>

        <div className="border rounded-xl overflow-auto max-h-[52vh]">
          <table className="w-full text-sm min-w-[820px]" data-testid="pay-calc-table">
            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 sticky top-0 z-10">
              <tr>
                <th className="p-2.5 w-10" />
                <th className="p-2.5 text-left font-medium">Load</th>
                <th className="p-2.5 text-left font-medium">Week</th>
                <th className="p-2.5 text-right font-medium">Load gross</th>
                <th className="p-2.5 text-left font-medium">Pay as</th>
                <th className="p-2.5 text-right font-medium">Rate</th>
                <th className="p-2.5 text-right font-medium">Driver pay</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading loads…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">No loads found for this driver's unit in this range</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className={`border-t border-slate-100 dark:border-slate-800 ${r.selected ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}`} data-testid={`pay-calc-row-${r.id}`}>
                  <td className="p-2.5 text-center">
                    <Checkbox checked={r.selected} onCheckedChange={(v) => update(r.id, { selected: !!v })} data-testid={`pay-calc-select-${r.id}`} />
                  </td>
                  <td className="p-2.5">
                    <div className="font-mono font-medium text-slate-900 dark:text-white flex items-center gap-1.5">
                      {r.load_reference}
                      {r.is_block && <span className="text-[11px] px-1.5 rounded bg-blue-600 text-white font-sans">Block</span>}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[260px]">{r.broker_name} · {r.pickup_location} → {r.delivery_location}</div>
                    {r.selected && r.paid_on_weeks?.length > 0 && (
                      <div className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1 mt-0.5" data-testid={`pay-calc-warning-${r.id}`}>
                        <AlertTriangle className="h-3 w-3" /> Also paid on week of {r.paid_on_weeks.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="p-2.5 whitespace-nowrap text-slate-600 dark:text-slate-300">{r.week_start}</td>
                  <td className="p-2.5 text-right font-mono">{money(r.payout_amount)}</td>
                  <td className="p-2.5">
                    <Select value={r.method} onValueChange={(v) => update(r.id, { method: v, rate: v === 'fixed' ? (r.is_block ? meta.block_pay || 0 : r.rate) : meta.default_pct || 0 })}>
                      <SelectTrigger className="h-8 w-[140px]" data-testid={`pay-calc-method-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed amount</SelectItem>
                        <SelectItem value="percentage">% of gross</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      {r.method === 'fixed' && <span className="text-slate-500">$</span>}
                      <Input type="number" step="0.01" className="h-8 w-24 text-right" value={r.rate} onChange={(e) => update(r.id, { rate: e.target.value })} data-testid={`pay-calc-rate-${r.id}`} />
                      {r.method === 'percentage' && <span className="text-slate-500">%</span>}
                    </div>
                  </td>
                  <td className={`p-2.5 text-right font-mono font-semibold ${r.selected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-400'}`} data-testid={`pay-calc-amount-${r.id}`}>{money(lineAmount(r))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:items-center gap-3">
          <div className="mr-auto text-base" data-testid="pay-calc-total">
            <span className="text-slate-600 dark:text-slate-300">{selected.length} load{selected.length === 1 ? '' : 's'} · Total pay </span>
            <span className="font-mono text-xl font-bold text-slate-900 dark:text-white">{money(total)}</span>
          </div>
          {meta.calc && (
            <Button variant="outline" onClick={reset} data-testid="pay-calc-reset"><RotateCcw className="h-4 w-4 mr-1.5" />Reset to automatic</Button>
          )}
          <Button onClick={save} disabled={saving || selected.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="pay-calc-save">
            {saving ? 'Saving…' : "Save as this week's pay"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
